import React from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';

import { CoreLink, Paragraph } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Icon } from '@uncinc/uncinc-react-kitchen-sink';

import { DEFAULT_LANGUAGE } from '../../../../config/constants';
import { getDayProgramEmail, getDayProgramTel } from '../../../../helpers/dayProgramHelpers';
import type { NodeProject } from '../../../../types/uncinc_profile/nodes';
import {
  ProjectHero,
  Block,
  DynamicMediaItem,
  Button,
  WysiwygField,
  Breadcrumbs,
} from '../../../../components';

import './style.scss';

interface NodeProjectDayProgramProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeProject>;
  className?: string;
}

const NodeProjectDayProgram: React.FC<NodeProjectDayProgramProps> = ({ entity, className = '', ...props }) => {
  const { t } = useTranslation();

  const dayProgram = entity.toJS();
  const title = dayProgram.title[0].value;
  const body = dayProgram.body[0]?.processed;
  const media = dayProgram.field_media_item[0];
  const location = dayProgram.field_location[0].entity;
  const paragraphs = dayProgram.field_paragraphs;

  const organization = dayProgram.field_partner[0];
  const organizationName = organization?.entity?.title[0].value;
  const organizationTel = organization?.entity?.field_telephone_number[0]?.value;
  const organizationEmail = organization?.entity?.field_email_address[0]?.value;

  // Organization always gets priority over day program links, even if not set.
  // We don't want to mix organization fields with day program link.
  const link = organization ? organization.entity?.field_link[0] : dayProgram.field_link[0];
  const linkUrl = link?.alias || link?.uri;
  const linkText = link?.title || t('pages.node_project.day_program.to_website');

  const tel = getDayProgramTel(organizationName, organizationTel, linkUrl);
  const email = getDayProgramEmail(organizationName, organizationEmail, linkUrl);

  // Checks if a link exists that is not from the organization and not a tel or email link.
  const isDayProgramLink = linkUrl !== undefined && !organization && !tel && !email;

  const classes = classNames('NodeProject', 'NodeProjectDayProgram', className);

  return (
    <article itemType="http://schema.org/WebPage" className={classes} {...props}>
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs />
          </div>
        </div>

        <div className="row">
          <div className="col-lg-7">
            <ProjectHero
              title={title}
              location={location}
              showLocationType={false}
            />

            {media && (
              <div className="NodeProjectDayProgram__media">
                <DynamicMediaItem media={media} imageAspectRatio="2_1" />
              </div>
            )}

            {body && (
              <WysiwygField
                className="NodeProjectDayProgram__body"
                property="schema:text"
                html={body}
              />
            )}

            {paragraphs.length > 0 && (
              <div className="NodeProjectDayProgram__paragraphs">
                {paragraphs.map(({ target_id: id, entity: paragraph }) => {
                  const language = paragraph.langcode[0].value || DEFAULT_LANGUAGE;

                  return (
                    <Paragraph
                      key={`paragraph:${id}:${language}`}
                      id={id}
                      entity={Immutable.Map(paragraph)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className="col-lg-5 col-xl-4 offset-xl-1">
            <Block type="secondary" className="NodeProjectDayProgram__contact-block">
              <h2 className="NodeProjectDayProgram__contact-block__title">
                {t('pages.node_project.day_program.contact')}
              </h2>

              <p className="NodeProjectDayProgram__contact-block__body">
                {t('pages.node_project.day_program.contact_text')}
              </p>

              {organizationName && (
                <h3 className="asH4 NodeProjectDayProgram__contact-block__organization-name">
                  {organizationName}
                </h3>
              )}

              {(tel || email || isDayProgramLink) && (
                <ul className="NodeProjectDayProgram__contact-block__info">
                  {tel && (
                    <li>
                      <CoreLink href={`tel:${tel}`} className="NodeProjectDayProgram__contact-block__info__link">
                        <Icon name="call_outline" />
                        <span>{tel}</span>
                      </CoreLink>
                    </li>
                  )}
                  {email && (
                    <li>
                      <CoreLink href={`mailto:${email}`} className="NodeProjectDayProgram__contact-block__info__link">
                        <Icon name="email_outline" />
                        <span>{email}</span>
                      </CoreLink>
                    </li>
                  )}
                  {/*
                    * Only show link if it is not from the organization, since this will show up as
                    * a button below.
                    */}
                  {isDayProgramLink && (
                    <li>
                      <CoreLink href={linkUrl} className="NodeProjectDayProgram__contact-block__info__link">
                        <Icon name="language" />
                        <span>{linkUrl}</span>
                      </CoreLink>
                    </li>
                  )}
                </ul>
              )}

              {/* Buttons only show if we have an organization. */}
              {organization && (
                <div className="NodeProjectDayProgram__contact-block__buttons">
                  {email && (
                    <Button href={`mailto:${email}`} type="primary" icon="email_outline">
                      {t('pages.node_project.day_program.register')}
                    </Button>
                  )}
                  {linkUrl && (
                    <Button href={linkUrl} type="secondary" icon="chevron_right">
                      {linkText}
                    </Button>
                  )}
                </div>
              )}
            </Block>
          </div>
        </div>
      </Container>
    </article>
  );
};

export default NodeProjectDayProgram;
